import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { otpService, OtpType } from '../../services/otpService';
import OtpInput from './components/OtpInput';
import Icon from '../../components/AppIcon';

type VerifyStep = 'input' | 'verify' | 'success';

interface LocationState {
  email?: string;
  phone?: string;
  type?: OtpType;
  returnTo?: string;
}

const VerifyOtp: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const [step, setStep] = useState<VerifyStep>('input');
  const [otpType, setOtpType] = useState<OtpType>(state?.type || 'phone');
  const [identifier, setIdentifier] = useState(state?.email || state?.phone || '');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // Check if this is an email link verification on mount
  useEffect(() => {
    const checkEmailLink = async () => {
      if (otpService.isEmailVerificationLink()) {
        console.log('📧 Email verification link detected');
        setIsLoading(true);
        setStep('verify');
        
        // Get email from URL params or local storage
        const urlParams = new URLSearchParams(window.location.search);
        const emailFromUrl = urlParams.get('email');
        const emailToVerify = emailFromUrl || window.localStorage.getItem('emailForSignIn') || '';
        
        if (emailToVerify) {
          setIdentifier(emailToVerify);
          setOtpType('email');
          
          // Auto-verify the email link
          try {
            const result = await otpService.verifyEmailOtp(emailToVerify);
            
            if (result.success && result.firebaseUser) {
              setSuccessMessage('✅ Email verified successfully!');
              setStep('success');
              
              // Navigate to dashboard after short delay
              setTimeout(() => {
                navigate(state?.returnTo || '/');
              }, 2000);
            } else {
              setError(result.error || 'Failed to verify email link');
              setStep('input');
            }
          } catch (err: any) {
            setError(err.message || 'Failed to verify email link');
            setStep('input');
          } finally {
            setIsLoading(false);
          }
        } else {
          setError('Email not found. Please enter your email.');
          setStep('input');
          setIsLoading(false);
        }
      }
    };

    checkEmailLink();
  }, []);

  // Cleanup reCAPTCHA on unmount
  useEffect(() => {
    return () => {
      otpService.cleanupRecaptcha();
    };
  }, []);

  // Auto-send OTP if identifier is pre-filled (but not for email links)
  useEffect(() => {
    if (identifier && (state?.email || state?.phone) && !otpService.isEmailVerificationLink()) {
      handleSendOtp();
    }
  }, []);

  const startCooldown = () => {
    setResendCooldown(60);
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!identifier) {
      setError(otpType === 'email' ? 'Please enter your email' : 'Please enter your phone number');
      return;
    }

    // Validation
    if (otpType === 'email' && !/\S+@\S+\.\S+/.test(identifier)) {
      setError('Please enter a valid email address');
      return;
    }

    if (otpType === 'phone' && !/^\+[1-9]\d{9,14}$/.test(identifier.replace(/\s/g, ''))) {
      setError('Please enter a valid phone number with country code (e.g., +91xxxxxxxxxx)');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const result = otpType === 'email'
        ? await otpService.sendEmailOtp(identifier)
        : await otpService.sendPhoneOtp(identifier, 'recaptcha-container');

      if (!result.success) {
        setError(result.error || 'Failed to send OTP');
        return;
      }

      setSuccessMessage(result.message || 'OTP sent successfully!');
      startCooldown();
      setTimeout(() => {
        setStep('verify');
        setSuccessMessage('');
      }, 500);
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (code?: string) => {
    const otpCode = code || otp;

    if (otpCode.length !== 6) {
      setError('Please enter a valid 6-digit OTP code');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const result = otpType === 'email'
        ? await otpService.verifyEmailOtp(identifier, otpCode)
        : await otpService.verifyPhoneOtp(identifier, otpCode);

      if (!result.success) {
        setError(result.error || 'Invalid OTP code');
        setOtp('');
        return;
      }

      // Clear rate limit
      otpService.clearRateLimit(identifier, otpType);

      // Update user profile with verified status
      if (otpType === 'phone' && result.firebaseUser) {
        // Phone verified via Firebase
        console.log('✅ Phone verified:', result.firebaseUser.phoneNumber);
        
        // Optionally update Supabase profile with phone
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('user_profiles')
            .update({ 
              phone: result.firebaseUser.phoneNumber,
              phone_verified: true,
              updated_at: new Date().toISOString()
            })
            .eq('id', user.id);
        }
      }

      if (otpType === 'email' && result.user) {
        // Email verified via Supabase
        console.log('✅ Email verified:', result.user.email);
      }

      setSuccessMessage('✅ Verification successful!');
      setStep('success');

      // Navigate after success - use safe navigation only
      setTimeout(() => {
        // Safe list of allowed redirect paths
        const allowedPaths = [
          '/brand-dashboard',
          '/creator-dashboard',
          '/agency-dashboard',
          '/admin-dashboard',
          '/settings',
          '/profile',
          '/'
        ];
        
        const returnPath = state?.returnTo;
        if (returnPath && typeof returnPath === 'string' && 
            returnPath.startsWith('/') && 
            !returnPath.includes('//') &&
            allowedPaths.some(path => returnPath.startsWith(path))) {
          navigate(returnPath);
        } else {
          navigate('/'); // Default to home
        }
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Verification failed');
      setOtp('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    // Reset reCAPTCHA for phone
    if (otpType === 'phone') {
      otpService.cleanupRecaptcha();
    }

    setOtp('');
    await handleSendOtp();
  };

  const maskedIdentifier = otpType === 'email'
    ? identifier.replace(/(.{2})(.*)(@.*)/, '$1***$3')
    : identifier.replace(/(\+\d{2})(\d*)(\d{4})/, '$1****$3');

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-primary rounded-full mix-blend-screen filter blur-3xl animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-primary-light rounded-full mix-blend-screen filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-primary rounded-full mix-blend-screen filter blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-md relative z-10">
        <div className="rounded-2xl shadow-2xl p-8 border border-dark-700 bg-dark-800 backdrop-blur-xl">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <img
                src="/assets/images/Noonx_logo_small.png"
                alt="NOONX Platform Logo"
                className="h-16 w-auto object-contain"
                loading="eager"
                fetchpriority="high"
              />
            </div>

            {step === 'input' && (
              <>
                <h1 className="text-3xl font-bold mb-2 text-white">Verify Your Account</h1>
                <p className="text-gray-400">We'll send you a verification code</p>
              </>
            )}

            {step === 'verify' && (
              <>
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                  <Icon 
                    name={otpType === 'email' ? 'Mail' : 'Smartphone'} 
                    size={32} 
                    className="text-primary" 
                  />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Enter Verification Code</h2>
                <p className="text-gray-400">Code sent to</p>
                <p className="text-primary font-medium">{maskedIdentifier}</p>
              </>
            )}

            {step === 'success' && (
              <>
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/10 rounded-full mb-4">
                  <Icon name="CheckCircle" size={48} className="text-green-500" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Verified!</h2>
                <p className="text-gray-400">Your {otpType} has been verified successfully</p>
              </>
            )}
          </div>

          {step === 'input' && (
            <form onSubmit={handleSendOtp} className="space-y-6">
              {/* Type Selector */}
              <div className="flex gap-2 p-1 bg-dark-700 rounded-lg">
                <button
                  type="button"
                  onClick={() => {
                    setOtpType('email');
                    setIdentifier('');
                    setError('');
                  }}
                  className={`
                    flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all
                    ${otpType === 'email'
                      ? 'bg-primary text-white shadow-lg'
                      : 'text-gray-400 hover:text-white'
                    }
                  `}
                >
                  <Icon name="Mail" size={16} className="inline mr-2" />
                  Email
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOtpType('phone');
                    setIdentifier('');
                    setError('');
                  }}
                  className={`
                    flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all
                    ${otpType === 'phone'
                      ? 'bg-primary text-white shadow-lg'
                      : 'text-gray-400 hover:text-white'
                    }
                  `}
                >
                  <Icon name="Smartphone" size={16} className="inline mr-2" />
                  Phone
                </button>
              </div>

              {/* Input */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {otpType === 'email' ? 'Email Address' : 'Phone Number'}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon 
                      name={otpType === 'email' ? 'Mail' : 'Phone'} 
                      size={18} 
                      className="text-gray-500" 
                    />
                  </div>
                  <input
                    type={otpType === 'email' ? 'email' : 'tel'}
                    value={identifier}
                    onChange={(e) => {
                      setIdentifier(e.target.value);
                      setError('');
                    }}
                    placeholder={otpType === 'email' ? 'you@example.com' : '+91 98765 43210'}
                    className={`
                      w-full pl-10 pr-4 py-3 rounded-lg
                      bg-dark-700 border text-white
                      placeholder-gray-500
                      focus:outline-none focus:ring-2 focus:ring-primary/50
                      ${error ? 'border-red-500' : 'border-dark-600 focus:border-primary'}
                    `}
                    disabled={isLoading}
                  />
                </div>
                {otpType === 'phone' && (
                  <p className="mt-1 text-xs text-gray-500">
                    Include country code (e.g., +91 for India)
                  </p>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {/* Success */}
              {successMessage && (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-green-400 text-sm">{successMessage}</p>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading || !identifier}
                className={`
                  w-full py-3 px-4 rounded-lg font-medium text-white
                  transition-all duration-200
                  ${isLoading || !identifier
                    ? 'bg-gray-600 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary-dark shadow-lg shadow-primary/30'
                  }
                `}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <Icon name="Loader2" size={20} className="animate-spin mr-2" />
                    Sending...
                  </span>
                ) : (
                  <>
                    <Icon name="Send" size={18} className="inline mr-2" />
                    Send Verification Code
                  </>
                )}
              </button>
            </form>
          )}

          {step === 'verify' && (
            <div className="space-y-6">
              {/* OTP Input */}
              <OtpInput
                value={otp}
                onChange={setOtp}
                onComplete={handleVerifyOtp}
                disabled={isLoading}
                error={error}
                autoFocus
              />

              {/* Error */}
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-sm text-center">{error}</p>
                </div>
              )}

              {/* Success */}
              {successMessage && (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-green-400 text-sm text-center">{successMessage}</p>
                </div>
              )}

              {/* Verify Button */}
              <button
                onClick={() => handleVerifyOtp()}
                disabled={isLoading || otp.length !== 6}
                className={`
                  w-full py-3 px-4 rounded-lg font-medium text-white
                  transition-all duration-200
                  ${isLoading || otp.length !== 6
                    ? 'bg-gray-600 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary-dark shadow-lg shadow-primary/30'
                  }
                `}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <Icon name="Loader2" size={20} className="animate-spin mr-2" />
                    Verifying...
                  </span>
                ) : (
                  'Verify Code'
                )}
              </button>

              {/* Resend */}
              <div className="text-center">
                <p className="text-gray-400 text-sm mb-2">Didn't receive the code?</p>
                <button
                  onClick={handleResend}
                  disabled={isLoading || resendCooldown > 0}
                  className={`
                    text-sm font-medium transition-colors
                    ${isLoading || resendCooldown > 0
                      ? 'text-gray-500 cursor-not-allowed'
                      : 'text-primary hover:text-primary-light'
                    }
                  `}
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                </button>
              </div>

              {/* Change Number/Email */}
              <button
                onClick={() => {
                  setStep('input');
                  setOtp('');
                  setError('');
                  otpService.cleanupRecaptcha();
                }}
                disabled={isLoading}
                className="w-full py-2 text-gray-400 hover:text-white text-sm transition-colors"
              >
                ← Change {otpType === 'email' ? 'email' : 'phone number'}
              </button>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center">
              <p className="text-gray-400 mb-4">Redirecting...</p>
              <div className="animate-pulse">
                <Icon name="Loader2" size={24} className="animate-spin text-primary mx-auto" />
              </div>
            </div>
          )}

          {/* Back Link */}
          <div className="mt-8 pt-6 border-t border-dark-600">
            <div className="flex items-center justify-center">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="text-sm text-gray-500 hover:text-primary transition-all font-medium"
              >
                ← Go Back
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* reCAPTCHA container */}
      <div id="recaptcha-container"></div>
    </div>
  );
};

export default VerifyOtp;
